<!-- No algorithms message -->
<div
  class="flex-row align-items-center justify-content-center"
  style="margin: 0 auto; padding: 48px"
  *ngIf="noAlgorithms && !loading"
>
  {{ 'table.algorithms.no_items' | translate }}
</div>

<!-- Table content -->
<table
  mat-table
  [dataSource]="items"
  multiTemplateDataRows
  class="spotlab-table"
  matSort
  *ngIf="!loading && !noAlgorithms"
>
  <ng-container matColumnDef="name" class="name-cell">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'core.name' | translate | uppercase }}
    </th>
    <td
      mat-cell
      *matCellDef="let algorithm"
      class="name-cell"
      [matTooltip]="algorithm.name"
      [matTooltipShowDelay]="2000"
    >
      {{ algorithm.name }}
    </td>
  </ng-container>

  <ng-container matColumnDef="version">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'core.version' | translate | uppercase }}
    </th>
    <td mat-cell *matCellDef="let algorithm" class="count-cell">
      {{ algorithm.version }}
    </td>
  </ng-container>

  <ng-container matColumnDef="organizations">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'title.organizations' | translate | uppercase }}
    </th>
    <td mat-cell *matCellDef="let algorithm" class="count-cell">
      {{ algorithm.numOrganizations }}
    </td>
  </ng-container>

  <ng-container matColumnDef="workspaces">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'title.workspaces' | translate | uppercase }}
    </th>
    <td mat-cell *matCellDef="let algorithm" class="count-cell">
      {{ algorithm.numWorkspaces }}
    </td>
  </ng-container>

  <ng-container matColumnDef="regulated">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'table.algorithms.columns.regulated' | translate | uppercase }}
    </th>
    <td mat-cell *matCellDef="let algorithm" class="count-cell">
      {{ algorithm.regulated ? ('core.yes' | translate | uppercase) : ('core.no' | translate | uppercase) }}
    </td>
  </ng-container>

  <ng-container matColumnDef="environment">
    <th mat-header-cell *matHeaderCellDef>
      {{ 'core.environment' | translate | uppercase }}
    </th>
    <td mat-cell *matCellDef="let algorithm" class="env-cell">
      <span
        class="env-badge"
        [class.env-cloud]="algorithm.environment === 'cloud'"
        [class.env-edge]="algorithm.environment === 'mobile'"
      >
        {{
          (algorithm.environment === 'cloud'
            ? 'core.cloud'
            : algorithm.environment === 'mobile'
            ? 'core.edge'
            : 'core.no_env'
          ) | translate }}
      </span>
    </td>
  </ng-container>

  <ng-container matColumnDef="status">
    <th mat-header-cell *matHeaderCellDef class="status-header-cell">
      {{ 'table.algorithms.columns.status' | translate | uppercase }}
    </th>
    <td mat-cell *matCellDef="let algorithm" class="status-cell last-data-cell">
      <span
        class="status-badge"
        [class.status-active]="!algorithm.deprecated"
        [class.status-deprecated]="algorithm.deprecated"
      >
        {{ (algorithm.deprecated ? 'table.algorithms.deprecated' : 'table.algorithms.active') | translate }}
      </span>
    </td>
  </ng-container>

  <ng-container matColumnDef="edit">
    <th mat-header-cell *matHeaderCellDef></th>
    <td mat-cell *matCellDef="let algorithm" class="icon-cell">
      <button
        class="icon-button"
        type="button"
        (click)="editClicked.emit(algorithm); $event.stopPropagation()"
        data-cy="algorithm-edit"
      >
        <i class="ri-pencil-line"></i>
      </button>
    </td>
  </ng-container>

  <!-- Expanded Content Column - spans across all columns -->
  <ng-container matColumnDef="expandedDetail">
    <td mat-cell *matCellDef="let algorithm" [attr.colspan]="visibleColumns.length">
      <div class="algorithm-detail" [@detailExpand]="algorithm === expandedAlgorithm ? 'expanded' : 'collapsed'">
        <div class="algorithm-detail-content">
          <!-- Description section -->
          <div class="description-section">
            <h3 class="description-title">{{ 'core.description' | translate }}</h3>
            <p class="description-text">{{ algorithm.description }}</p>
          </div>

          <!-- Error state -->
          <div class="usage-error" *ngIf="isUsageError(algorithm.id)">
            <mat-icon color="warn">warning</mat-icon>
            <p class="error-text">{{ getErrorMessage(algorithm.id) | translate }}</p>
          </div>

          <!-- Usage section -->
          <div class="usage-section">
            <h3 class="usage-title">{{ 'table.algorithms.usages_title' | translate }}</h3>

            <!-- Loading state -->
            <div class="usage-loading" *ngIf="isUsageLoading(algorithm.id)">
              <mat-spinner diameter="40"></mat-spinner>
            </div>

            <!-- Usage data loaded in a material card with slightly rounded corners -->
            <div class="usage-content" *ngIf="!isUsageLoading(algorithm.id) && !isUsageError(algorithm.id)">
              <mat-card class="usage-card">
                <ts-algorithm-usage-table
                  [algorithmId]="algorithm.id"
                  [pipelines]="getUsage(algorithm.id)?.pipelines || {}"
                  [language]="language"
                  [noItems]="hasNoPipelines(algorithm.id)"
                  (editClicked)="onEditPipelineStep($event.algorithmId, $event.pipeline)"
                  (deleteClicked)="onDeleteFromPipelineStep($event.algorithmId, $event.pipeline)"
                  (navigateToPipeline)="navigateToPipeline.emit($event)"
                ></ts-algorithm-usage-table>
                <button
                  mat-stroked-button
                  color="accent"
                  [class.deprecate-button]="!algorithm.deprecated"
                  [class.undeprecate-button]="algorithm.deprecated"
                  type="button"
                  (click)="deprecateClicked.emit(algorithm); $event.stopPropagation()"
                  data-cy="algorithm-deprecate"
                >
                  {{
                    algorithm.deprecated
                      ? ('table.algorithms.undeprecate' | translate | uppercase)
                      : ('table.algorithms.deprecate' | translate | uppercase)
                  }}
                </button>
              </mat-card>
            </div>
          </div>
        </div>
      </div>
    </td>
  </ng-container>

  <tr mat-header-row *matHeaderRowDef="visibleColumns"></tr>
  <tr
    mat-row
    *matRowDef="let algorithm; columns: visibleColumns"
    class="algorithm-row"
    [class.expanded-row]="expandedAlgorithm === algorithm"
    (click)="toggleRow(algorithm)"
  ></tr>
  <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
</table>
